import React, { useEffect, useRef, useState } from "react";

import gsap from "gsap";
import { useGSAP } from '@gsap/react'

import CustomEase from "gsap/CustomEase";
import SplitType from "split-type";
import "./dsProjects.css"; // We will create this file next
import crosswords from '../../resources/project-images/crosswords.png'
import customer_segment from '../../resources/project-images/customer-segment.png'
import customer_segment2 from '../../resources/project-images/customer-segment-2.jpg'
import genetics from '../../resources/project-images/genetics.png'
import home_price from '../../resources/project-images/home-price.png'
import student_performance from '../../resources/project-images/student-performance.png'
import student_stress from '../../resources/project-images/student-stress.png'
import tic_tac_toe from '../../resources/project-images/tic-tac-toe.png'
import lunar_lander from '../../resources/project-images/lunar-lander.png'
import movie_recomm from '../../resources/project-images/movie-recom.jpg'
import quickscope from '../../resources/selected-works/quick-scope.jpg'
import llm from '../../resources/selected-works/llm.png'
import ailibrarian from '../../resources/selected-works/ai-librarian.jpg'



// Register GSAP plugins
gsap.registerPlugin(CustomEase);
CustomEase.create("hop", "0.9, 0, 0.1, 1");


const desc = [
  "An end-to-end machine learning system designed to predict student stress levels from large-scale, real-world behavioral data. The project unifies highly inconsistent, multi-modal data collected from 500+ heterogeneous sources, including 2M+ sensor events (app usage, motion, microphone), surveys, and ~32K self-reports. Raw event-level logs are transformed into interpretable daily behavioral features, enabling reliable stress prediction and actionable wellness insights.",

  "A data analysis and insight generation project focused on extracting meaningful customer behavior patterns from transactional and engagement data. Combines exploratory analysis, statistical reasoning, and feature engineering to surface drivers of customer value, preferences, and engagement, supporting informed product and business decisions.",

  "A production-ready, multi-model NLP system for intelligent book discovery and recommendation. The system combines semantic vector search, zero-shot classification, and emotion-aware filtering powered by transformer-based models and ChromaDB to deliver context-aware results. Deployed as a scalable FastAPI service on Google Cloud Run, supported by a fully automated CI/CD pipeline using GitHub Actions and Google Artifact Registry for serverless, versioned inference.",

  "A real-time social media and news intelligence platform hosted on AWS, designed to surface live discussions and generate topic-specific, unbiased reports across platforms. The system fine-tunes a 1B-parameter language model using LoRA (r=32) with Unsloth, optimized for 4-bit inference on a custom dataset. Backed by an end-to-end MLOps pipeline with automated data generation, Dockerized builds, CI/CD, and deployment to AWS EC2 via ECR, reducing inference cost and latency.",

  "A machine learning–driven property valuation system for the Bangalore real-estate market, trained on ~13K property records. The pipeline includes data preprocessing, feature engineering, and GridSearchCV-based hyperparameter tuning, achieving ~86% accuracy. The model is exposed via a FastAPI service behind NGINX, enabling real-time price predictions in a production environment.",

  "A transformer-based language model (~60M parameters) designed and trained from scratch for controlled text generation. The architecture includes 384-dim token and positional embeddings, causal self-attention, 6-head multi-head attention, residual connections, LayerNorm, and MLP blocks across 6 transformer layers with a 128-token context window, tuned specifically for generating coherent and creative children’s stories.",

  "An AI reasoning project from Harvard’s CS50 AI course focused on modeling genetic inheritance under uncertainty. Uses Bayesian networks and probabilistic inference to compute gene distributions and trait expression probabilities, demonstrating structured reasoning and decision-making with incomplete information.",

  "An unsupervised learning project analyzing 500K+ real-world e-commerce transactions collected over a year to uncover customer behavior and spending dynamics. The system derives 6 meaningful customer segments, translating clustering outcomes into actionable business strategies for targeted marketing, churn risk identification, and revenue optimization.",

  "A constraint-based AI system from CS50 AI that solves crossword puzzles using Constraint Satisfaction Problems (CSPs). Implements constraint propagation, backtracking search, and optimization techniques to efficiently generate valid crossword solutions under complex structural and lexical constraints.",

  "An end-to-end student performance prediction system combining feature processing, supervised learning, and hyperparameter tuning to achieve ~85% accuracy. Delivered as a Dockerized application with an automated build/run pipeline and an interactive frontend for real-time performance visualization.",

  "An adversarial game-playing AI from CS50 AI implementing Minimax search to achieve optimal play. The system evaluates game states recursively to guarantee perfect decision-making against human opponents.",

  "A reinforcement learning project from the DeepLearning.AI Machine Learning Specialization focused on training an autonomous agent to safely land a spacecraft. The project emphasizes reward design, policy learning, and iterative optimization within a simulated control environment.",

  "A recommendation system from the DeepLearning.AI Machine Learning Specialization that delivers personalized movie suggestions using collaborative filtering and content-based techniques. Models user preferences and item similarities to produce data-driven recommendations."
];


// --- Configuration Data ---
const items = [
  "Stress Predictor",
  "Customer Undersatanding",
  'AI Librarian',
  'Quickscope',
  "Real Estate Price Prediction ",
  'Custom Language Model',
  "Genetics",
  "Customer Segmentation",
  "crosswords Solver",
  "Performance Predictor ",
  "Tic Tac Toe",
  "Lunar Lander",
  "Movie Recommendation",
];

const imageUrls = [
  student_stress,
  customer_segment,
  ailibrarian,
  quickscope,
  home_price,
  llm,
  genetics,
  customer_segment2,
  crosswords,
  student_performance,
  tic_tac_toe,
  lunar_lander,
  movie_recomm,
  // "https://cdn.cosmos.so/0f164449-f65e-4584-9d62-a9b3e1f4a90a?format=jpeg",
  // "https://cdn.cosmos.so/74ccf6cc-7672-4deb-ba13-1727b7dc6146?format=jpeg",
  // "https://cdn.cosmos.so/2f49a117-05e7-4ae9-9e95-b9917f970adb?format=jpeg",
  // "https://cdn.cosmos.so/7b5340f5-b4dc-4c08-8495-c507fa81480b?format=jpeg",
  // "https://cdn.cosmos.so/f733585a-081e-48e7-a30e-e636446f2168?format=jpeg",
  // "https://cdn.cosmos.so/47caf8a0-f456-41c5-98ea-6d0476315731?format=jpeg",
  // "https://cdn.cosmos.so/f99f8445-6a19-4a9a-9de3-ac382acc1a3f?format=jpeg",
];

const DataScienceProjects = () => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const paneRef = useRef(null);
  const [mobile,setMobile] = useState(window.innerWidth <= 768)
  const [isExpanded,setIsExpanded] = useState(false)
  console.log(isExpanded)

  // We use a ref to store all the "global" logic variables to keep them accessible
  // inside the effect without stale closures, and to clean them up on unmount.

  
  useEffect(() => {
    const handleResize = () => {
      // Note: Usually "mobile" is determined by width, 
      // but I've kept it as 768 height to match your logic.
      setMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    
    // Clean up the listener when the component unmounts
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const engineRef = useRef({
    settings: {
      baseWidth: mobile?200:400,
      smallHeight: mobile?165:330,
      largeHeight: mobile?250:500,
      itemGap: mobile?32:65,
      hoverScale: 1.05,
      expandedScale: mobile?1:0.4,
      dragEase: 0.075,
      momentumFactor: mobile?400:200,
      bufferZone: 3,
      borderRadius: 0,
      vignetteSize: 0,
      vignetteStrength: 0.7,
      overlayOpacity: 1,
      overlayEaseDuration: 0.8,
      zoomDuration: 0.6,
    },
    state: {
      isDragging: false,
      startX: 0,
      startY: 0,
      targetX: 0,
      targetY: 0,
      currentX: 0,
      currentY: 0,
      dragVelocityX: 0,
      dragVelocityY: 0,
      lastDragTime: 0,
      mouseHasMoved: false,
      visibleItems: new Set(),
      lastUpdateTime: 0,
      lastX: 0,
      lastY: 0,
      isExpanded: false,
      activeItem: null,
      activeItemId: null,
      canDrag: true,
      originalPosition: null,
      expandedItem: null,
      overlayAnimation: null,
      titleSplit: null,
      reqId: null, // requestAnimationFrame ID
    },
  });

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    const overlay = overlayRef.current;
    const projectTitleElement = titleRef.current;
    const projectDescElement = descRef.current
    const engine = engineRef.current;
    const { settings, state } = engine;

    // Derived variables
    let itemSizes = [
      { width: settings.baseWidth, height: settings.smallHeight },
      { width: settings.baseWidth, height: settings.largeHeight },
    ];
    let columns = 4;
    const itemCount = items.length;
    let cellWidth = settings.baseWidth + settings.itemGap;
    let cellHeight =
      Math.max(settings.smallHeight, settings.largeHeight) + settings.itemGap;

    // --- Helper Functions ---

    const updateCSSVariables = () => {
      document.documentElement.style.setProperty(
        "--border-radius",
        `${settings.borderRadius}px`
      );
      document.documentElement.style.setProperty(
        "--vignette-size",
        `${settings.vignetteSize}px`
      );
      document.documentElement.style.setProperty(
        "--hover-scale",
        settings.hoverScale
      );

      // Page vignette logic
      const strength = settings.vignetteStrength;
      const size = settings.vignetteSize;

      document.documentElement.style.setProperty(
        "--page-vignette-size",
        `${size * 1.5}px`
      );
      document.documentElement.style.setProperty(
        "--page-vignette-color",
        `rgba(0,0,0,${strength * 0.7})`
      );

      document.documentElement.style.setProperty(
        "--page-vignette-strong-size",
        `${size * 0.75}px`
      );
      document.documentElement.style.setProperty(
        "--page-vignette-strong-color",
        `rgba(0,0,0,${strength * 0.85})`
      );

      document.documentElement.style.setProperty(
        "--page-vignette-extreme-size",
        `${size * 0.4}px`
      );
      document.documentElement.style.setProperty(
        "--page-vignette-extreme-color",
        `rgba(0,0,0,${strength})`
      );

      // Update existing items hover transition
      const items = document.querySelectorAll(".item img");
      items.forEach((img) => {
        img.style.transition = "transform 0.3s ease";
      });
    };

    // --- Core Logic ---

    const getItemSize = (row, col) => {
      const sizeIndex = Math.abs((row * columns + col) % itemSizes.length);
      return itemSizes[sizeIndex];
    };

    const getItemId = (col, row) => `${col},${row}`;

    const getItemPosition = (col, row) => ({
      x: col * cellWidth,
      y: row * cellHeight,
    });

    const setAndAnimateTitle = (title) => {
      if (state.titleSplit) state.titleSplit.revert();
      projectTitleElement.textContent = title;
      state.titleSplit = new SplitType(projectTitleElement, { types: "words" });
      gsap.set(state.titleSplit.words, { y: "100%" });
    };

    const animateTitleIn = () => {
      if (state.titleSplit && state.titleSplit.words) {
        gsap.fromTo(
          state.titleSplit.words,
          { y: "100%", opacity: 0 },
          { y: "0%", opacity: 1, duration: 1, stagger: 0.1, ease: "power3.out" }
        );
      }
    };

    const animateTitleOut = () => {
      if (state.titleSplit && state.titleSplit.words) {
        gsap.to(state.titleSplit.words, {
          y: "-100%",
          opacity: 0,
          duration: 1,
          stagger: 0.1,
          ease: "power3.out",
        });
      }
    };

    const animateOverlayIn = () => {
      if (state.overlayAnimation) state.overlayAnimation.kill();
      state.overlayAnimation = gsap.to(overlay, {
        opacity: settings.overlayOpacity,
        duration: settings.overlayEaseDuration,
        ease: "power2.inOut",
        overwrite: true,
      });
    };

    const animateOverlayOut = () => {
      if (state.overlayAnimation) state.overlayAnimation.kill();
      state.overlayAnimation = gsap.to(overlay, {
        opacity: 0,
        duration: settings.overlayEaseDuration,
        ease: "power2.inOut",
      });
    };

    // --- Interaction Logic (Expand/Collapse) ---

    const closeExpandedItem = (fromHistory = false) => {
      if (state.isClosing || !state.expandedItem || !state.originalPosition) return;

      // 2. HISTORY SYNC (from previous step):
      if (fromHistory !== true && window.history.state?.expanded) {
        state.isClosing = true; // Lock it immediately
        window.history.back();
        return; 
      }

      // 3. LOCK: Mark as closing for the duration of the animation
      state.isClosing = true;

      // ... Existing animation logic starts here ...
      animateTitleOut();
      animateOverlayOut();

      // Fade in other items
      document.querySelectorAll(".item").forEach((el) => {
        if (el.id !== state.activeItemId) {
          gsap.to(el, {
            opacity: 1,
            duration: settings.overlayEaseDuration,
            delay: 0.3,
            ease: "power2.inOut",
          });
        }
      });

      const originalItem = document.getElementById(state.activeItemId);
      if (originalItem) {
        const nameElement = originalItem.querySelector(".item-name");
        const numberElement = originalItem.querySelector(".item-number");
        nameElement.textContent = state.originalPosition.nameText;
        numberElement.textContent = state.originalPosition.numberText;
        originalItem.querySelector(".item-caption").style.opacity = "0";
      }

      const {
        rect: originalRect,
        width: originalWidth,
        height: originalHeight,
      } = state.originalPosition;

      gsap.to(state.expandedItem, {
        width: originalWidth,
        height: originalHeight,
        x: originalRect.left + originalWidth / 2 - window.innerWidth / 2,
        y: originalRect.top + originalHeight / 2 - window.innerHeight / 2,
        duration: settings.zoomDuration,
        ease: "hop",
        onComplete: () => {
          if (originalItem) {
            // Reconstruct clone for animation
            const captionElement = originalItem.querySelector(".item-caption");
            const captionClone = document.createElement("div");
            captionClone.className = "caption-clone";
            captionClone.innerHTML = captionElement.innerHTML;

            const captionRect = captionElement.getBoundingClientRect();
            captionClone.style.position = "fixed";
            captionClone.style.left = `${captionRect.left}px`;
            captionClone.style.bottom = `${
              window.innerHeight - captionRect.bottom
            }px`;
            captionClone.style.width = `${captionRect.width}px`;
            captionClone.style.padding = "10px";
            captionClone.style.zIndex = "10002";
            document.body.appendChild(captionClone);

            const nameClone = captionClone.querySelector(".item-name");
            const numberClone = captionClone.querySelector(".item-number");
            nameClone.style.overflow = "hidden";
            numberClone.style.overflow = "hidden";

            const nameCloneSplit = new SplitType(nameClone, { types: "words" });
            const numberCloneSplit = new SplitType(numberClone, {
              types: "words",
            });

            projectDescElement.textContent = ""

            gsap.set('.ds-proj-desc',{
              opacity:0,
            }) 

            gsap.set([nameCloneSplit.words, numberCloneSplit.words], {
              y: "100%",
              opacity: 0,
            });

            gsap.to(nameCloneSplit.words, {
              y: "0%",
              opacity: 1,
              duration: 0.7,
              stagger: 0.03,
              ease: "power3.out",
            });
            gsap.to(numberCloneSplit.words, {
              y: "0%",
              opacity: 1,
              duration: 0.7,
              stagger: 0.02,
              delay: 0.05,
              ease: "power3.out",
              onComplete: () => {
                captionElement.style.opacity = "1";
                if (captionClone.parentNode)
                  document.body.removeChild(captionClone);
              },
            });
            gsap.to(state.expandedItem, {
              // ... inside your existing GSAP object ...
              onComplete: () => {
                // ... existing cleanup code ...

                // 4. RESET: Unlock the flag when animation finishes
                state.isClosing = false; 
                
                // ... rest of cleanup (setIsExpanded, etc) ...
              }
            });
          }

          if (state.expandedItem && state.expandedItem.parentNode) {
            document.body.removeChild(state.expandedItem);
          }

          if (originalItem) originalItem.style.visibility = "visible";

          // Reset State
          setIsExpanded(false)
          state.expandedItem = null;
          state.isExpanded = false;
          state.activeItem = null;
          state.originalPosition = null;
          state.activeItemId = null;
          state.canDrag = true;
          container.style.cursor = "grab";
          state.dragVelocityX = 0;
          state.dragVelocityY = 0;
          overlay.classList.remove("active");
        },
      });
    };

    const expandItem = (item, itemIndex) => {
      // 1. HISTORY PUSH:
      // Tell the browser we are entering a "sub-view".
      // This makes the Back button active.
      window.history.pushState({ expanded: true }, "");

      state.isExpanded = true;
      setIsExpanded(state.isExpanded);
      console.log('set to true')
      state.activeItem = item;
      state.activeItemId = item.id;
      state.canDrag = false;
      container.style.cursor = "auto";

      const imgSrc = item.querySelector("img").src;
      const titleIndex = itemIndex % items.length;
      const itemWidth = parseInt(item.dataset.width);
      const itemHeight = parseInt(item.dataset.height);

      setAndAnimateTitle(items[titleIndex]);
      projectDescElement.textContent = desc[titleIndex]

      // Caption Logic
      const nameElement = item.querySelector(".item-name");
      const numberElement = item.querySelector(".item-number");
      const nameText = nameElement.textContent;
      const numberText = numberElement.textContent;

      const captionClone = item.querySelector(".item-caption").cloneNode(true);
      captionClone.classList.add("caption-clone");

      // Setup clones for animation
      const nameClone = captionClone.querySelector(".item-name");
      const numberClone = captionClone.querySelector(".item-number");
      const nameCloneSplit = new SplitType(nameClone, { types: "words" });
      const numberCloneSplit = new SplitType(numberClone, { types: "words" });

      const captionRect = item
        .querySelector(".item-caption")
        .getBoundingClientRect();
      captionClone.style.left = `${captionRect.left}px`;
      captionClone.style.bottom = `${
        window.innerHeight - captionRect.bottom
      }px`;
      captionClone.style.width = `${captionRect.width}px`;
      captionClone.style.zIndex = "10002";
      document.body.appendChild(captionClone);

      item.querySelector(".item-caption").style.opacity = "0";

      gsap.to(nameCloneSplit.words, {
        y: "100%",
        opacity: 0,
        duration: 0.6,
        stagger: 0.03,
        ease: "power3.in",
      });
      gsap.to(numberCloneSplit.words, {
        y: "100%",
        opacity: 0,
        duration: 0.6,
        stagger: 0.02,
        delay: 0.05,
        ease: "power3.in",
        onComplete: () => {
          if (captionClone.parentNode) document.body.removeChild(captionClone);
        },
      });
      gsap.to('.ds-proj-desc',{
        opacity:1,
      })

      const rect = item.getBoundingClientRect();
      state.originalPosition = {
        id: item.id,
        rect,
        imgSrc,
        width: itemWidth,
        height: itemHeight,
        nameText,
        numberText,
      };

      overlay.classList.add("active");
      animateOverlayIn();

      // Create Expanded Item
      const expandedItem = document.createElement("div");
      //   document.querySelector(".ds-proj").appendChild(expandedItem);
      expandedItem.className = "expanded-item";
      expandedItem.style.width = `${itemWidth}px`;
      expandedItem.style.height = `${itemHeight}px`;
      expandedItem.style.zIndex = "10000";
      expandedItem.style.borderRadius = `var(--border-radius, 0px)`;

      const img = document.createElement("img");
      img.src = imgSrc;
      img.width ='20vh'
      expandedItem.appendChild(img);

      expandedItem.addEventListener("click", closeExpandedItem);
      document.body.appendChild(expandedItem);
      state.expandedItem = expandedItem;

      // Fade out other items
      document.querySelectorAll(".item").forEach((el) => {
        if (el !== state.activeItem) {
          gsap.to(el, {
            opacity: 0,
            duration: settings.overlayEaseDuration,
            ease: "power2.inOut",
          });
        }
      });

      const viewportWidth = window.innerWidth;
      const targetWidth = viewportWidth * settings.expandedScale;
      const aspectRatio = itemHeight / itemWidth;
      const targetHeight = targetWidth * aspectRatio;

      gsap.delayedCall(0.5, animateTitleIn);

      gsap.fromTo(
        expandedItem,
        {
          width: itemWidth,
          height: itemHeight,
          x: rect.left + itemWidth / 2 - window.innerWidth / 2,
          y: rect.top + itemHeight / 2 - window.innerHeight / 2,
        },
        {
          width: targetWidth,
          height: targetHeight,
          x: 0,
          y: 0,
          duration: settings.zoomDuration,
          ease: "hop",
        }
      );
    };

    const handleItemClick = (item, itemNum) => {
      if (state.isExpanded) {
        if (state.expandedItem) closeExpandedItem();
      } else {
        expandItem(item, itemNum);
      }
    };

    // --- Core Grid Logic ---

    const updateVisibleItems = () => {
      const buffer = settings.bufferZone;
      const viewWidth = window.innerWidth * (1 + buffer);
      const viewHeight = window.innerHeight * (1 + buffer);

      const startCol = Math.floor(
        (-state.currentX - viewWidth / 2) / cellWidth
      );
      const endCol = Math.ceil((-state.currentX + viewWidth * 1.5) / cellWidth);
      const startRow = Math.floor(
        (-state.currentY - viewHeight / 2) / cellHeight
      );
      const endRow = Math.ceil(
        (-state.currentY + viewHeight * 1.5) / cellHeight
      );

      const currentItems = new Set();

      for (let row = startRow; row <= endRow; row++) {
        for (let col = startCol; col <= endCol; col++) {
          const itemId = getItemId(col, row);
          currentItems.add(itemId);

          if (state.visibleItems.has(itemId)) continue;
          if (state.activeItemId === itemId && state.isExpanded) continue;

          // Create Item
          const itemSize = getItemSize(row, col);
          const position = getItemPosition(col, row);
          const itemNum = Math.abs((row * columns + col) % itemCount);

          const item = document.createElement("div");
          item.className = "item";
          item.id = itemId;
          item.style.width = `${itemSize.width}px`;
          item.style.height = `${itemSize.height}px`;
          item.style.left = `${position.x}px`;
          item.style.top = `${position.y}px`;
          item.dataset.col = col;
          item.dataset.row = row;
          item.dataset.width = itemSize.width;
          item.dataset.height = itemSize.height;

          const imageContainer = document.createElement("div");
          imageContainer.className = "item-image-container";

          const img = document.createElement("img");
          img.src = imageUrls[itemNum % imageUrls.length];
          img.alt = `Image ${itemNum + 1}`;
          // Apply current hover scale logic
          img.style.transition = "transform 0.3s ease";

          imageContainer.appendChild(img);
          item.appendChild(imageContainer);

          const captionElement = document.createElement("div");
          captionElement.className = "item-caption";

          const nameElement = document.createElement("div");
          nameElement.className = "item-name";
          nameElement.textContent = items[itemNum];

          const numberElement = document.createElement("div");
          numberElement.className = "item-number";
          numberElement.textContent = `#${(itemNum + 1)
            .toString()
            .padStart(5, "0")}`;

          captionElement.appendChild(nameElement);
          captionElement.appendChild(numberElement);
          item.appendChild(captionElement);
          

          item.addEventListener("click", () => {
            if (state.mouseHasMoved || state.isDragging) return;
            handleItemClick(item, itemNum);
          });

          canvas.appendChild(item);
          state.visibleItems.add(itemId);
        }
      }

      // Cleanup
      state.visibleItems.forEach((itemId) => {
        if (
          !currentItems.has(itemId) ||
          (state.activeItemId === itemId && state.isExpanded)
        ) {
          const item = document.getElementById(itemId);
          if (item && item.parentNode === canvas) {
            canvas.removeChild(item);
          }
          state.visibleItems.delete(itemId);
        }
      });
    };

    const animate = () => {
      if (state.canDrag) {
        const ease = settings.dragEase;
        state.currentX += (state.targetX - state.currentX) * ease;
        state.currentY += (state.targetY - state.currentY) * ease;

        if (canvas) {
          canvas.style.transform = `translate(${state.currentX}px, ${state.currentY}px)`;
        }

        const now = Date.now();
        const distMoved = Math.sqrt(
          Math.pow(state.currentX - state.lastX, 2) +
            Math.pow(state.currentY - state.lastY, 2)
        );

        if (distMoved > 100 || now - state.lastUpdateTime > 120) {
          updateVisibleItems();
          state.lastX = state.currentX;
          state.lastY = state.currentY;
          state.lastUpdateTime = now;
        }
      }
      state.reqId = requestAnimationFrame(animate);
    };

    // --- Event Listeners ---

    const onMouseDown = (e) => {
      if (!state.canDrag) return;
      state.isDragging = true;
      state.mouseHasMoved = false;
      state.startX = e.clientX;
      state.startY = e.clientY;
      container.style.cursor = "grabbing";
    };

    const onMouseMove = (e) => {
      if (!state.isDragging || !state.canDrag) return;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;

      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) state.mouseHasMoved = true;

      const now = Date.now();
      const dt = Math.max(10, now - state.lastDragTime);
      state.lastDragTime = now;

      state.dragVelocityX = dx / dt;
      state.dragVelocityY = dy / dt;

      state.targetX += dx;
      state.targetY += dy;
      state.startX = e.clientX;
      state.startY = e.clientY;
    };

    const onMouseUp = () => {
      if (!state.isDragging) return;
      state.isDragging = false;
      if (state.canDrag) {
        container.style.cursor = "grab";
        if (
          Math.abs(state.dragVelocityX) > 0.1 ||
          Math.abs(state.dragVelocityY) > 0.1
        ) {
          state.targetX += state.dragVelocityX * settings.momentumFactor;
          state.targetY += state.dragVelocityY * settings.momentumFactor;
        }
      }
    };

    const onTouchStart = (e) => {
      if (!state.canDrag) return;
      state.isDragging = true;
      state.mouseHasMoved = false;
      state.startX = e.touches[0].clientX;
      state.startY = e.touches[0].clientY;
    };

    const onTouchMove = (e) => {
      if (!state.isDragging || !state.canDrag) return;
      const dx = e.touches[0].clientX - state.startX;
      const dy = e.touches[0].clientY - state.startY;

      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) state.mouseHasMoved = true;

      state.targetX += dx;
      state.targetY += dy;
      state.startX = e.touches[0].clientX;
      state.startY = e.touches[0].clientY;
    };

    const onTouchEnd = () => {
      state.isDragging = false;
    };

    const onResize = () => {
      if (state.isExpanded && state.expandedItem && state.originalPosition) {
        const viewportWidth = window.innerWidth;
        const targetWidth = viewportWidth * settings.expandedScale;
        const originalWidth = state.originalPosition.width;
        const originalHeight = state.originalPosition.height;
        const aspectRatio = originalHeight / originalWidth;
        const targetHeight = targetWidth * aspectRatio;

        gsap.to(state.expandedItem, {
          width: targetWidth,
          height: targetHeight,
          duration: 0.3,
          ease: "power2.out",
        });
      } else {
        updateVisibleItems();
      }
    };

    const onKeyDown = (e) => {
      if ((e.key === "h" || e.key === "H") && paneRef.current) {
        const el = paneRef.current.element;
        el.style.display = el.style.display === "none" ? "" : "none";
      }
    };

    const onOverlayClick = () => {
      if (state.isExpanded) closeExpandedItem();
    };

    // --- Bootstrapping ---

    updateCSSVariables();
    updateVisibleItems();
    animate();
    // setTimeout(initTweakpane, 500);

    // Attach listeners
    container.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    container.addEventListener("touchstart", onTouchStart);
    window.addEventListener("touchmove", onTouchMove);
    window.addEventListener("touchend", onTouchEnd);
    
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKeyDown);
    overlay.addEventListener("click", onOverlayClick);

    const handlePopState = () => {
      if (engineRef.current.state.isExpanded) {
        // Force reset the flag so the actual close function can run
        engineRef.current.state.isClosing = false; 
        closeExpandedItem(true);
      }
    };
    const onWheel = () => {
      // Trigger close only if expanded and not already animating out
      if (state.isExpanded && !state.isClosing) {
        closeExpandedItem();
      }
    };
    window.addEventListener("wheel", onWheel);

    container.addEventListener("mousedown", onMouseDown);
    // ... other listeners ...
    window.addEventListener("popstate", handlePopState);



    // Cleanup
    return () => {
      cancelAnimationFrame(state.reqId);
      if (paneRef.current) paneRef.current.dispose();

      container.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      container.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKeyDown);
      overlay.removeEventListener("click", onOverlayClick);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("wheel", onWheel);

      if (state.expandedItem && state.expandedItem.parentNode) {
        document.body.removeChild(state.expandedItem);
      }
    };
    
  }, []);

  
  useGSAP(() => {
    gsap.to(".work-letters", {
      y: 0,
      stagger: 0.2,
      ease: "power4.out",
      duration: 1.5,
      opacity: 1,
      scrollTrigger: {
        trigger: ".work-txt-container",
        start: "top 45%",
        end: "50% top",
        toggleActions: "play play play reverse",
      },
  });
  });

  useGSAP(() => {
    gsap.from(".ds-proj-root", {
      marginTop: 250,
      ease: "power4.out",
      duration: 1.6,
      opacity: 0.5,
      scrollTrigger: {
        // trigger: ".work-txt-container",
        trigger: ".ds-proj-root",
        start: "top bottom",
        end: "50% top",
        toggleActions: "play pause pause reverse",
      onLeaveBack:()=>{
          gsap.to('.ds-proj-header',{
            opacity:0,
            duration:0.5
          })
        },
      },
      onComplete:()=>{
        gsap.to('.ds-proj-header',{
          opacity:0.8,
          duration:0.5
        })
      }
    });
  });

  const workText = 'WORKS'.split('').map((char, index) => (
    <div className='work-letters leading-[90%] opacity-0 translate-y-[100%] bg-gradient-to-t from-[#333] to-white bg-clip-text text-transparent' key={index}>
      <div className='inline-block'>{char}</div>
    </div>
  ))
  return (
    <div id ='works' >
      <h1 className="work-txt-container h-[100dvh] flex justify-center items-center font-humane-black text-[12.5rem] sm:text-[15rem] md:text-[20rem] lg:text-[30rem] xl:text-[40rem]">
        <span className="flex overflow-hidden pt-8">{workText}</span>
      </h1>
      <div className="ds-proj-section mt-[300px] h-[110svh]">
        <div className=" ds-proj-header opacity-0 pp-supply-mono text-sm md:text-lg pr-4 text-right mb-4"> Drag and click to explore projects</div>
        
        <div className="ds-proj-root">

          <div className="ds-proj">
            {/* --- Header Section --- */}
            <div className="header">
              <div className="nav-section">
                <div className="logo-container">
                  <div className="logo-circles">
                  </div>
                </div>
              </div>

              <div className="social-section">
                <h3>+Social</h3>
                <ul>
                  <li>
                    <a
                      href="https://instagram.com/filipz__"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Instagram
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://x.com/filipz"
                      target="_blank"
                      rel="noreferrer"
                    >
                      X / Twitter
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://linkedin.com/in/filipzrnzevic"
                      target="_blank"
                      rel="noreferrer"
                    >
                      LinkedIn
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* --- Main Animation Container --- */}
            {/* We attach containerRef here for the drag events */}
            <div className="container scale-100" ref={containerRef}>
              {/* We attach canvasRef here for GSAP to move and append items */}
              <div className="canvas" id="canvas" ref={canvasRef}></div>
              {/* We attach overlayRef here for the fade-in background */}
              <div className="overlay" id="overlay" ref={overlayRef}>
                
              </div>
            </div>

            {/* --- Project Title --- */}
            <div className="project-title">
              {/* We attach titleRef here for SplitType animation */}
              <p ref={titleRef}></p>
              
            </div>

            <div className="ds-proj-desc w-full flex justify-center  fixed bottom-12 opacity-0 text-white text-md md:text-xl px-8 z-[10008]">
              <p className="md:max-w-[80%] text-center mix-blend-difference bg-[#000000a0]" ref={descRef}></p>
            </div>

            

            {/* --- Page Vignette Effects --- */}
            {/* These correspond to the settings controlled by Tweakpane */}
            <div className="page-vignette-container">
              <div className="page-vignette"></div>
              <div className="page-vignette-strong"></div>
              <div className="page-vignette-extreme"></div>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default DataScienceProjects;
